// src/App.jsx
import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import useWakeUp from './hooks/useWakeUp';
import ROUTES from './routes';

// ✅ Lazy load all pages
const Layout = lazy(() => import('./layout/Layout'));
const Home = lazy(() => import('./pages/Home'));
const ImageConvert = lazy(() => import('./pages/ImageConvert'));
const ConvertToPng = lazy(() => import('./pages/ConvertToPng'));
const ConvertToJpg = lazy(() => import('./pages/ConvertToJpg'));
const ConvertToWebp = lazy(() => import('./pages/ConvertToWebp'));
const ConvertToAvif = lazy(() => import('./pages/ConvertToAvif'));
const ConvertToPdf = lazy(() => import('./pages/ConvertToPdf'));
const ConvertToSvg = lazy(() => import('./pages/ConvertToSvg'));
const ImageCompressor = lazy(() => import('./pages/ImageCompressor'));
const ImageResize = lazy(() => import('./pages/ImageResize'));
const EditImagePage = lazy(() => import('./pages/EditImagePage'));
const MergePdf = lazy(() => import('./pages/MergePdf'));
const SplitPdf = lazy(() => import('./pages/SplitPdf'));
const RotatePdf = lazy(() => import('./pages/RotatePdf'));
const DeletePdf = lazy(() => import('./pages/DeletePdf'));
const ReorderPdf = lazy(() => import('./pages/ReorderPdf'));
const CropImage = lazy(() => import('./pages/CropImage'));
const OcrConvert = lazy(() => import('./pages/OcrConvert'));
const ImgToWordConvert = lazy(() => import('./pages/ImgToWordConvert'));
const ImgToExcelConvert = lazy(() => import('./pages/ImgToExcelConvert'));
const FaviconGenerator = lazy(() => import('./pages/FaviconGenerator'));
const TextFaviconGenerator = lazy(() => import('./components/TextFavicon'));
const WpsToWord = lazy(() => import('./pages/WpsToWord'));
const WpsToDoc = lazy(() => import('./pages/WpsToDoc'));
const WpsToPdf = lazy(() => import('./pages/WpsToPdf'));
const PasswordRemover = lazy(() => import('./pages/PasswordRemover'));
const PasswordSetter = lazy(() => import('./pages/PasswordSetter'));
const PdfToWordConvert = lazy(() => import('./pages/PdfToWordConvert'));
const PdfToExcelConvert = lazy(() => import('./pages/PdfToExcelConvert'));
// const FileConvert = lazy(() => import('./pages/FileCovert'));
const FileExamples = lazy(() => import('./pages/FileExamples'));
const AviVideoSample = lazy(() => import('./pages/fileexamples/AviVideoSamples'));
const MovVideoSample = lazy(() => import('./pages/fileexamples/MovVideoSample'));
const Mp4VideoSample = lazy(() => import('./pages/fileexamples/Mp4VideoSample'));
const OggVideoSample = lazy(() => import('./pages/fileexamples/OggVideoSample'));
const WmvVideoSample = lazy(() => import('./pages/fileexamples/WmvVideoSample'));
const WebmVideoSample = lazy(() => import('./pages/fileexamples/WebmVideoSaple'));
const MP3AudioSample = lazy(() => import('./pages/fileexamples/Mp3AudioSample'));
const WAVAudioSample = lazy(() => import('./pages/fileexamples/WavAudioSample'));
const OggAudioSample = lazy(() => import('./pages/fileexamples/OggAudioSample'));
const JpgSamples = lazy(() => import('./pages/fileexamples/JpgSamples'));
const PngSamples = lazy(() => import('./pages/fileexamples/PngSamples'));
const GIFSamples = lazy(() => import('./pages/fileexamples/GifSamples'));
const TIFFSamples = lazy(() => import('./pages/fileexamples/TiffSamples'));
const IcoSamples = lazy(() => import('./pages/fileexamples/IcoSamples'));
const SVGSamples = lazy(() => import('./pages/fileexamples/SvgSamples'));
const WebpSamples = lazy(() => import('./pages/fileexamples/WebpSamples'));
const DocSamples = lazy(() => import('./pages/fileexamples/DocSamples'));
const WpsSamples = lazy(() => import('./pages/fileexamples/WpsSamples'));
const XlsSamples = lazy(() => import('./pages/fileexamples/XlsSamples'));
const PdfSamples = lazy(() => import('./pages/fileexamples/PdfSamples'));
const PptSamples = lazy(() => import('./pages/fileexamples/PptSamples'));
const OdtSamples = lazy(() => import('./pages/fileexamples/OdtSamples'));
const OdsSamples = lazy(() => import('./pages/fileexamples/OdsSamples'));
const OdpSamples = lazy(() => import('./pages/fileexamples/OdpSamples'));
const RtfSamples = lazy(() => import('./pages/fileexamples/RtfSamples'));
const TextSamples = lazy(() => import('./pages/fileexamples/TextSamples'));
const ZipSamples = lazy(() => import('./pages/fileexamples/ZipSamples'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
function App() {
  useWakeUp();
  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center min-h-screen text-indigo-500 text-lg animate-pulse">
          Loading tool...
        </div>
      }
    >
      <Routes>
        <Route path={ROUTES.HOME} element={<Layout />}>
          <Route index element={<Home />} />
          <Route path={ROUTES.IMAGE_CONVERT} element={<ImageConvert />} />
          <Route path={ROUTES.CONVERT_TO_PNG} element={<ConvertToPng />} />
          <Route path={ROUTES.CONVERT_TO_JPG} element={<ConvertToJpg />} />
          <Route path={ROUTES.CONVERT_TO_WEBP} element={<ConvertToWebp />} />
          <Route path={ROUTES.CONVERT_TO_AVIF} element={<ConvertToAvif />} />
          <Route path={ROUTES.CONVERT_TO_PDF} element={<ConvertToPdf />} />
          <Route path={ROUTES.CONVERT_TO_SVG} element={<ConvertToSvg />} />
          <Route path={ROUTES.IMAGE_COMPRESSOR} element={<ImageCompressor />} />
          <Route path={ROUTES.IMAGE_RESIZE} element={<ImageResize />} />
          <Route path={ROUTES.IMAGE_RESIZE_CUSTOM} element={<EditImagePage />} />
          <Route path={ROUTES.MERGE_PDF} element={<MergePdf />} />
          <Route path={ROUTES.SPLIT_PDF} element={<SplitPdf />} />
          <Route path={ROUTES.ROTATE_PDF} element={<RotatePdf />} />
          <Route path={ROUTES.DELETE_PDF} element={<DeletePdf />} />
          <Route path={ROUTES.REORDER_PDF} element={<ReorderPdf />} />
          <Route path={ROUTES.CROP_IMAGE} element={<CropImage />} />
          <Route path={ROUTES.IMAGE_TO_Word} element={<ImgToWordConvert />} />
          <Route path={ROUTES.IMAGE_TO_Excel} element={<ImgToExcelConvert />} />
          <Route path={ROUTES.Favicon_Text_Generator} element={<TextFaviconGenerator/>} />
          <Route path={ROUTES.Favicon_Generator} element={<FaviconGenerator />} />
          {/* <Route path={ROUTES.WPS_TO_WORD} element={<WpsToWord />} />
          <Route path={ROUTES.WPS_TO_DOC} element={<WpsToDoc/>} />
          <Route path={ROUTES.WPS_TO_PDF} element={<WpsToPdf/>} /> */}
          <Route path={ROUTES.PASSWORD_REMOVER} element={<PasswordRemover/>} />
          <Route path={ROUTES.PASSWORD_SETTER} element={<PasswordSetter/>} />
          <Route path={ROUTES.PDF_TO_Word} element={<PdfToWordConvert />} />
          <Route path={ROUTES.PDF_TO_Excel} element={<PdfToExcelConvert />} />
          {/* <Route path={ROUTES.FILE_CONVERT} element={<FileConvert/>} /> */}
          <Route path={ROUTES.FILE_EXAMPLES} element={<FileExamples/>} />
          <Route path="/avi-sample-videos" element={<AviVideoSample/>} />
          <Route path="/mov-sample-videos" element={<MovVideoSample/>} />
          <Route path="/mp4-sample-videos" element={<Mp4VideoSample/>} />
          <Route path="/ogg-sample-videos" element={<OggVideoSample/>} />
          <Route path="/wmv-sample-videos" element={<WmvVideoSample/>} />
          <Route path="/webm-sample-videos" element={<WebmVideoSample/>} />
          <Route path="/MP3-sample-Audios" element={<MP3AudioSample/>} />
          <Route path="/wav-sample-Audios" element={<WAVAudioSample/>} />
          <Route path="/ogg-sample-audios" element={<OggAudioSample/>} />
          <Route path="/jpg-sample-images" element={<JpgSamples/>} />
          <Route path="/png-sample-images" element={<PngSamples/>} />
          <Route path="/gif-sample-images" element={<GIFSamples/>} />
          <Route path="/tiff-sample-images" element={<TIFFSamples/>} />
          <Route path="/ico-sample-images" element={<IcoSamples/>} />
          <Route path="/svg-sample-images" element={<SVGSamples/>} />
          <Route path="/webp-sample-images" element={<WebpSamples/>} />
          <Route path="/doc-sample-files" element={<DocSamples/>} />
          <Route path="/wps-sample-files" element={<WpsSamples/>} />
          <Route path="/xls-sample-files" element={<XlsSamples/>} />
          <Route path="/pdf-sample-files" element={<PdfSamples/>} />
          <Route path="/ppt-sample-files" element={<PptSamples/>} />
          <Route path="/odt-sample-files" element={<OdtSamples/>} />
          <Route path="/ods-sample-files" element={<OdsSamples/>} />
          <Route path="/odp-sample-files" element={<OdpSamples/>} />
          <Route path="/rtf-sample-files" element={<RtfSamples/>} />
          <Route path="/text-sample-files" element={<TextSamples/>} />
          <Route path="/zip-sample-files" element={<ZipSamples/>} />
          <Route path={ROUTES.ABOUT} element={<AboutUs />} />
          <Route path="/comingsoon" element={<ComingSoon />} />
          {/* <Route path={ROUTES.DA_PA_CHECKER} element={<DAChecker/>} /> */}
          {/* <Route path={ROUTES.FEEDBACK} element={<Feedback/>} />
          <Route path={ROUTES.SIGNUP} element={<Signup />} />
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.SUBSCRIPTION} element={<Subscription />} /> */}
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
